import * as THREE from "three";

/**
 * Obstacle shapes for collision / pathfinding.
 * - circle: radius in XZ plane
 * - rect:   axis-aligned half-extents in X and Z
 */
export type Obstacle =
  | {
      type: "circle";
      center: THREE.Vector3;
      radius: number;
      repulsion: number;
    }
  | {
      type: "rect";
      center: THREE.Vector3;
      halfWidth: number;
      halfDepth: number;
      /** Rotation around Y in radians. Defaults to 0 (axis-aligned). */
      rotation?: number;
      repulsion: number;
    };

/**
 * Tuning workflow:
 * 1. Toggle "Show Colliders" in the leva debug panel (top-right).
 * 2. Compare each red shape to its real 3D model in the scene.
 * 3. Edit values below — Vite HMR refreshes the page on save instantly.
 * 4. For long/narrow models (e.g. SFO terminal) prefer "rect" with
 *    asymmetric halfWidth/halfDepth. For round/uniform models, "circle".
 */
export const SCENE_OBSTACLES: Obstacle[] = [
  // === Architecture Section ===
  { type: "rect", center: new THREE.Vector3(-5, 0, 6), halfWidth: 3.4, halfDepth: 1.5, repulsion: 1 },   // 777 Airport
  { type: "circle", center: new THREE.Vector3(-5, 0, 15), radius: 2.2, repulsion: 1 },  // Nanning
  { type: "rect", center: new THREE.Vector3(-8, 0, 22), halfWidth: 3, halfDepth: 1.5, repulsion: 1 }, // Foshan
  { type: "circle", center: new THREE.Vector3(-15, 0, 30), radius: 3, repulsion: 1 }, // SUNAC
  { type: "rect", center: new THREE.Vector3(-4.1, 0, 42.6), halfWidth: 2.6, halfDepth: 1.6, repulsion: 1 },  // Lize E05
  { type: "rect", center: new THREE.Vector3(-2.6, 0, 37.5), halfWidth: 1.2, halfDepth: 1.8, repulsion: 1 },  // Lize E06
  { type: "rect", center: new THREE.Vector3(-12.5, 0, 42.7), halfWidth: 2.8, halfDepth: 1.7, repulsion: 1 },  // Lize E01
  { type: "circle", center: new THREE.Vector3(-38, 0, 14), radius: 15, repulsion: 1 }, // SFO (long terminal — example rect)
  { type: "rect", center: new THREE.Vector3(-31.3, 0, 28), halfWidth: 0.9, halfDepth: 2.5, repulsion: 1 }, // SFO
  { type: "rect", center: new THREE.Vector3(-20, 0, 45), halfWidth: 0.9, halfDepth: 1.1, repulsion: 1 }, // Belgrade
  { type: "circle", center: new THREE.Vector3(-30, 0, 40), radius: 2.1, repulsion: 1 }, // Xian
  { type: "rect", center: new THREE.Vector3(-30, 0, 44.7), halfWidth: 2.6, halfDepth: 1.8, repulsion: 1 }, // Xian
  { type: "circle", center: new THREE.Vector3(-40, 0, 40), radius: 3, repulsion: 1 }, // Shenzhen

  // === Virtual Architecture Section ===
  { type: "rect", center: new THREE.Vector3(5, 0, -5), halfWidth: 1.5, halfDepth: 1.5, repulsion: 1 },   // Fine Arts
  { type: "rect", center: new THREE.Vector3(15, 0, -5), halfWidth: 1.5, halfDepth: 1.5, repulsion: 1 },  // Convergence
  { type: "rect", center: new THREE.Vector3(4.7, 0, -16.7), halfWidth: 1, halfDepth: 3.2, repulsion: 1 },  // Virtual Vibes
  { type: "rect", center: new THREE.Vector3(15, 0, -15), halfWidth: 2.5, halfDepth: 2.5, repulsion: 1 }, // DCL DAO HQ
  { type: "circle", center: new THREE.Vector3(5, 0, -10), radius: 0.8, repulsion: 1 },  // OVA Logo

  // === Product Section ===
  { type: "circle", center: new THREE.Vector3(-25, 0, -15), radius: 1, repulsion: 1 }, // Infini Loops

  // === Projects Section (web/AI billboards — flat rectangles, rotated 45° to match the frames) ===
  { type: "rect", center: new THREE.Vector3(10, 0, 20), halfWidth: 5.5, halfDepth: 1, rotation: Math.PI / 4, repulsion: 1 }, // OVA.DESIGN frame
  { type: "rect", center: new THREE.Vector3(25, 0, 15), halfWidth: 5.5, halfDepth: 1, rotation: Math.PI / 4, repulsion: 1 }, // Parallel AI frame
  { type: "rect", center: new THREE.Vector3(23, 0, 33), halfWidth: 5.5, halfDepth: 1, rotation: Math.PI / 4, repulsion: 1 }, // CRIA frame

  // === Center Logo ===
  { type: "rect", center: new THREE.Vector3(-8, 0, -8.3), halfWidth: 1.2, halfDepth: 0.6, repulsion: 1.2 }, // CaiLogo
];
